/**
 * Аккордеон. Стили — `src/scss/components/_accordion.scss`.
 *
 * Скрипт только переключает класс и состояние ARIA. Анимация
 * на CSS (`grid-template-rows`), поэтому здесь нет ни
 * `scrollHeight`, ни `style.maxHeight`, ни пересчёта на `resize`.
 *
 * Разметка — три уровня внутри панели обязательны, причина
 * в шапке `_accordion.scss`:
 *
 *   <div class="accordion" data-accordion>
 *     <div class="accordion__item">
 *       <button class="accordion__trigger" type="button">
 *         Заголовок
 *         <span class="accordion__icon">…</span>
 *       </button>
 *       <div class="accordion__panel">
 *         <div class="accordion__inner">
 *           <div class="accordion__content">…</div>
 *         </div>
 *       </div>
 *     </div>
 *   </div>
 *
 * Атрибуты корня:
 *   data-accordion          обязательный, по нему ищется группа
 *   data-accordion-single   режим одиночки: открытие закрывает соседей
 *
 * Атрибут элемента:
 *   data-accordion-open     раскрыт при загрузке
 */

const ROOT = '[data-accordion]';
const ITEM = '.accordion__item';
const TRIGGER = '.accordion__trigger';
const PANEL = '.accordion__panel';
const OPEN = 'accordion__item--open';

/** Единственный источник состояния — класс, его же читает CSS. */
const isOpen = (item) => item.classList.contains(OPEN);

function setOpen(item, open) {
    item.classList.toggle(OPEN, open);

    // `aria-expanded` — на кнопке, а не на элементе: атрибут
    // описывает то, чем управляет сам триггер
    const trigger = item.querySelector(TRIGGER);
    if (trigger) trigger.setAttribute('aria-expanded', String(open));

    const panel = item.querySelector(PANEL);
    if (!panel) return;

    // Убирает содержимое закрытой панели из обхода табом
    // и из дерева доступности
    panel.inert = !open;
}

function initGroup(root) {
    // Защита от повторной инициализации: два слушателя на одной
    // кнопке дают двойной toggle, и элемент не открывается вовсе.
    // Так было на `/pcc/`, починено в `a46b89a`
    if (root.dataset.accordionReady === '1') return;
    root.dataset.accordionReady = '1';

    const single = root.hasAttribute('data-accordion-single');
    const items = [...root.querySelectorAll(ITEM)];
    if (!items.length) return;

    // Стартовое состояние задаётся разметкой. В режиме одиночки
    // открытым останется только первый помеченный элемент
    let alreadyOpen = false;
    for (const item of items) {
        const wantOpen = item.hasAttribute('data-accordion-open') && !(single && alreadyOpen);
        if (wantOpen) alreadyOpen = true;
        setOpen(item, wantOpen);
    }

    // Делегирование: один слушатель на группу. Переживает
    // подмену разметки внутри
    root.addEventListener('click', (event) => {
        const trigger = event.target.closest(TRIGGER);
        if (!trigger || !root.contains(trigger)) return;

        const item = trigger.closest(ITEM);
        if (!item || !items.includes(item)) return;

        const willOpen = !isOpen(item);

        if (single && willOpen) {
            for (const other of items) {
                if (other !== item && isOpen(other)) setOpen(other, false);
            }
        }

        setOpen(item, willOpen);
    });
}

/** Точка входа. Вызывается из `src/main.js` после готовности DOM. */
export default function Accordion() {
    const roots = document.querySelectorAll(ROOT);
    if (!roots.length) return;

    roots.forEach(initGroup);
}
